import { Alert, Grid, Paper } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { Box } from '@mui/system';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { MemberSwishStatusSearchFilter } from '../models/memberSwishStatusSearchFilter';
import { MemberSwishStatusDataGrid } from '../components/memberSwishStatusDataGrid';
import { SwishTabNavigation } from './swishTabNavigation';

// Kopplade medlemmar (Swish Återkommande)
export const MemberSwishStatusOverviewPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);

  const [memberSwishStatusFilter, setMemberSwishStatusFilter] = useLocalStorage(LocalStorageKeys.MemberSwishStatusFilter, {
    sortCol: 'nextInvoiceAfter',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as MemberSwishStatusSearchFilter);

  const searchFilter: MemberSwishStatusSearchFilter = useMemo(() => {
    return {
      filter: memberSwishStatusFilter?.filter,
      pageSize: memberSwishStatusFilter?.pageSize,
      pageIndex: memberSwishStatusFilter?.pageIndex,
      sortCol: memberSwishStatusFilter?.sortCol,
      sortDir: memberSwishStatusFilter?.sortDir,
    } as MemberSwishStatusSearchFilter;
  }, [
    memberSwishStatusFilter?.filter,
    memberSwishStatusFilter?.pageIndex,
    memberSwishStatusFilter?.pageSize,
    memberSwishStatusFilter?.sortCol,
    memberSwishStatusFilter?.sortDir,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setMemberSwishStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setMemberSwishStatusFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setMemberSwishStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setMemberSwishStatusFilter, tempSearchString]);

  return (
    <GridWrapper>
      <Grid
        item
        xs={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <MainContentHeader title={t('invoice:swishStatus.header')} />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <SwishTabNavigation />
          <Alert severity='info' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {t('invoice:swishStatus.alertInfoText')}
          </Alert>
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.filter || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
          />
          <Box className='section-inset'>
            <MemberSwishStatusDataGrid
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) =>
                setMemberSwishStatusFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }))
              }
            />
          </Box>
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
